import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Boundary, LngLat } from '../../shared/protocol';
import {
  TRAVEL_MODES,
  TRAVEL_MODE_LABEL,
  barrierPresetFor,
  regenerateOfferFor,
  shouldRegenerateOnModeChange,
  type BoundaryOrigin,
  type TravelMode,
  requiredCoverageM,
} from '../../shared/travelMode';
// v26 BOLT-145: the client neither harvests nor measures coverage any more. The
// synthetic lattice is all that is left, and only for the keyless case.
import { syntheticBarriers } from '../map/barriers';
// v25 BOLT-144 — one source for every word the lobby says about the area.
import {
  coarserMode,
  compositionSentence,
  crossingLabel,
  failureHeadline,
  nextStepLabel,
  nextStepsFor,
  type TurfFailure,
  type TurfNextStep,
} from '../../shared/map/boundaryDescribe';
import { DEFAULT_SETTINGS } from '../../shared/constants';
import type { Map as MlMap } from 'maplibre-gl';
import { formatAreaStr } from '../util/format';

/**
 * v23 — Home Turf's lobby surface.
 *
 * The north star (Jake): "A user should have fun if they create a new game,
 * have a friend scan to join, then click Start." So the PRIMARY view is tiny:
 * three mode icons and ONE map button.
 *
 * v27 BOLT-150 collapsed the rest. There used to be an "Adjust map" disclosure
 * hiding manual drawing and Recent maps, plus a Redraw beside it and a third
 * button in the lobby - Jake: "three similar buttons visible 'draw custom',
 * 'redraw', and 'redraw play area'. Very confusing." Two of them opened the
 * same screen. Everything optional now lives in the full-screen editor, which
 * the button and the map preview both open.
 */

export interface HomeTurfProps {
  center: LngLat | null;
  boundary: Boundary | null;
  origin: BoundaryOrigin;
  mode: TravelMode;
  building: boolean;
  failed: boolean;
  shortCoverage?: boolean;
  /** v25 BOLT-144: WHY the last build failed, so the panel offers the right step. */
  reason?: TurfFailure;
  /** v25 BOLT-144: what the area on screen is made of, from its own ring. */
  composition?: string[];
  /** v26 BOLT-143: safety notices the server derived from this exact polygon. */
  safetyNotices?: string[];
  onModeChange: (mode: TravelMode) => void;
  /** v27 BOLT-150: opens the full-screen editor. Host-only (Jake). */
  onEdit: () => void;
  canEdit: boolean;
  onRegenerate: () => void;
  onWiden?: () => void;
  areaSqMeters?: number;
  /**
   * v28 BOLT-155: the map preview, rendered INSIDE this section.
   *
   * Jake: "the organization of the 'Play area' is strange. The map appears
   * outside that container." It did: the preview lived in `Lobby.tsx` while
   * every control that describes it lived here, so the heading labelled a
   * control that was not what the heading said. It is passed in rather than
   * built here because a JOINER sees the preview and never sees this section.
   */
  preview?: ReactNode;
}

const MODE_ICON: Record<TravelMode, string> = { foot: '🚶', bike: '🚲', car: '🚗' };
/** v25 BOLT-144: the picker says what it DOES now, so the word is on the button. */
const MODE_SHORT: Record<TravelMode, string> = { foot: 'Walk', bike: 'Bike', car: 'Drive' };

export function HomeTurf(props: HomeTurfProps) {
  const { mode, origin, building, failed, shortCoverage, boundary } = props;
  // v26 BOLT-148: the barrier checklist is GONE. Five road-tier toggles were
  // the implementation showing through, they only ever made sense while mode
  // meant a tier, and they were never built on iOS at all. They also fight
  // BOLT-147, which works by CHANGING the tier when the player's own tier
  // encloses nothing: a pinned set either blocks the fallback or gets silently
  // overridden. Mode is the only barrier-related control now, which is what v25
  // always said it was.
  const [offerRegenerate, setOfferRegenerate] = useState(false);
  const area = props.areaSqMeters ?? 0;
  const composition = props.composition ?? [];
  const reason: TurfFailure = shortCoverage ? 'need-coverage' : (props.reason ?? 'not-enclosed');

  const changeMode = (next: TravelMode) => {
    props.onModeChange(next);
    // AUTO regenerates silently; CUSTOM is the player's map, so we OFFER
    // instead of stomping it. They are never surprised by a redraw.
    //
    // With NO boundary there is nothing to stomp, so a mode tap just builds.
    // That is the way back from clearing the map in the editor: without it a
    // host who cleared and walked out would be stuck with no area and no
    // one-tap way to get one.
    if (!boundary) return props.onRegenerate();
    if (!shouldRegenerateOnModeChange(origin)) setOfferRegenerate(true);
  };

  const takeStep = (step: TurfNextStep) => {
    if (step === 'widen') props.onWiden?.();
    else if (step === 'coarser') {
      const next = coarserMode(mode);
      if (next) changeMode(next);
    } else props.onEdit();
  };

  return (
    <div className="panel section-gap home-turf" data-testid="home-turf">
      <div className="row between">
        <div className="field__label" style={{ margin: 0 }}>
          Play area
        </div>
        {boundary && !building && (
          <span className="mono" style={{ fontSize: '0.72rem', color: 'var(--moss)' }} data-testid="boundary-area">
            {origin === 'auto' ? 'Auto' : 'Custom'}
          </span>
        )}
      </div>

      {/* v25 BOLT-144 #1: the picker presents TRAVEL, not road classes. It set a
          road tier before, which is why the same setting meant something
          different in Amsterdam, Manhattan and McKinney; it sets a target SIZE
          now, so the label and the behaviour finally agree and the word belongs
          on the button rather than hidden in an aria-label.
          v28 BOLT-155 (Jake): "the walk/bike/drive are labeled as 'Play area',
          but that's the Mode, which influences play area." Right: the container
          heading is the SECTION, and the control gets its own name. */}
      <div className="field__label" style={{ marginTop: '0.7rem', marginBottom: '0.35rem' }} data-testid="travel-mode-label">
        Travel mode
      </div>
      <div className="row" style={{ gap: '0.5rem' }} data-testid="travel-modes">
        {TRAVEL_MODES.map((m) => (
          <button
            key={m}
            className={`btn mode-chip${mode === m ? ' btn--signal' : ''}`}
            style={{ width: 'auto', padding: '0.5rem 0.9rem' }}
            data-testid={`mode-${m}`}
            data-active={mode === m}
            aria-label={TRAVEL_MODE_LABEL[m]}
            title={TRAVEL_MODE_LABEL[m]}
            onClick={() => changeMode(m)}
          >
            {/* The spinner is in the MARKUP the moment a build starts, but CSS
                holds it invisible for 250 ms. With the v27 build cache most
                toggles land in ~100 ms, and a spinner that appears and vanishes
                inside that reads as a glitch - it makes a fast app feel
                unstable. Fast stays invisibly fast; slow gets honest feedback.
                Doing the delay in CSS rather than a timer also keeps it in the
                rendered output, so it is testable. */}
            {building && mode === m ? (
              <span className="mode-spinner" aria-hidden="true" data-testid={`mode-building-${m}`} />
            ) : (
              <span aria-hidden="true">{MODE_ICON[m]}</span>
            )}{' '}
            {MODE_SHORT[m]}
          </button>
        ))}
      </div>

      {/* v28 BOLT-155: Mode -> MAP -> (Regenerate / Keep mine) -> Edit Map, all
          in one container. The map is what every control here is about, so it
          sits with them rather than above the box that names them. */}
      {props.preview}

      {/* v25 BOLT-144 #4: the size, in the unit the decision is made in.
          Nobody can picture 0.315 mi²; everybody can picture a 12 minute walk,
          and it comes off the same speed model that sized the map. */}
      {boundary && !building && area > 0 && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.85rem' }} data-testid="turf-crossing">
          {crossingLabel(area, mode)}
        </div>
      )}

      {/* v25 BOLT-144 #3: what the border is MADE of, sampled off the polygon on
          screen. It proves the area is not arbitrary (the trust the field report
          says is broken) and it teaches what a good boundary looks like.

          AUTO ONLY, and that is not a detail: a reused recent map or a
          hand-drawn one arrives by `set_boundary` without ever going through a
          harvest, so the last auto build's names would still be sitting here
          describing a completely different polygon. The server enforces the
          same rule for iOS by keying the composition to the boundary OBJECT. */}
      {boundary && !building && origin === 'auto' && composition.length > 0 && (
        <div
          className="eyebrow"
          style={{ marginTop: '0.25rem', color: 'var(--moss)' }}
          data-testid="turf-composition"
        >
          {compositionSentence(composition)}
        </div>
      )}

      {/* v26 BOLT-143 (Jake): "if we are auto generating maps, we want to
          minimize unsafe play areas." We choose the area, so we own what is
          inside it. Above the fold and never dismissible: BOLT-147 can hand a
          foot player a genuinely large map, and with no fallback banner of its
          own this is the only thing that says what is in it. */}
      {!building && (props.safetyNotices ?? []).length > 0 && (
        <div className="section-gap" data-testid="turf-safety">
          {(props.safetyNotices ?? []).map((n) => (
            <div key={n} className="notice notice--caution" data-testid="turf-safety-notice">
              {n}
            </div>
          ))}
        </div>
      )}

      {building && (
        <div className="eyebrow" style={{ marginTop: '0.6rem' }} data-testid="turf-building">
          Building your area…
        </div>
      )}

      {/* BOLT-124 #9 / v25 BOLT-144 #5: sparse or messy data must land somewhere
          useful. Phase 1 made rural correctly return NOTHING where it used to
          invent a rectangle, so "no map here" is a state real players will meet
          and every one of its causes now ends in a button, never a dead end. */}
      {failed && !building && (
        <div className="banner banner--signal" style={{ marginTop: '0.6rem' }} data-testid="turf-fallback">
          <span className="banner__detail">
            {failureHeadline(reason)}
            {boundary ? ' The area shown is from your last setting.' : ''}
          </span>
          <div className="row" style={{ gap: '0.4rem', marginTop: '0.4rem' }}>
            {nextStepsFor(reason, mode).map((step) => (
              <button
                key={step}
                className={`btn${step === 'draw' ? ' btn--ghost' : ''}`}
                style={{ width: 'auto' }}
                data-testid={`turf-step-${step}`}
                onClick={() => takeStep(step)}
              >
                {nextStepLabel(step, mode)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* v28 BOLT-155 (Jake): "'Regenerate for X' wraps the Regenerate word into
          two lines which looks bad." The banner is a flex ROW, so at 320 px the
          question was squeezed between the two buttons until it broke after its
          first word. It is a column now: the question owns a full line, the
          buttons share the one below, and neither label may wrap. */}
      {offerRegenerate && (
        <div className="banner regen-offer" style={{ marginTop: '0.6rem' }} data-testid="regenerate-offer">
          <span className="banner__detail regen-offer__q">{regenerateOfferFor(mode)}</span>
          <div className="regen-offer__actions">
            <button
              className="btn btn--signal"
              data-testid="regenerate-yes"
              onClick={() => {
                setOfferRegenerate(false);
                props.onRegenerate();
              }}
            >
              Regenerate
            </button>
            <button className="btn btn--ghost" data-testid="regenerate-no" onClick={() => setOfferRegenerate(false)}>
              Keep mine
            </button>
          </div>
        </div>
      )}

      {/* v27 BOLT-150 (Jake): "we currently have three similar buttons visible
          'draw custom', 'redraw', and 'redraw play area'. Very confusing."
          Two of them opened the same screen and the third regenerated the auto
          area while wearing almost the same word. One button now, and the map
          itself is the other way in. */}
      <button
        className="btn section-gap"
        data-testid="edit-map"
        disabled={!props.canEdit}
        onClick={props.onEdit}
      >
        {boundary ? 'Edit Map' : 'Draw Map'}
      </button>

    </div>
  );
}

/**
 * The AUTO side of the flow, as a hook: on lobby load (and on every mode change
 * while still AUTO) build an area with no user action at all.
 */
export function useAutoBoundary(opts: {
  center: LngLat | null;
  mode: TravelMode;
  origin: BoundaryOrigin;
  enabled: boolean;
  /** The headstart the room is actually set to: it is what sizes the map. */
  headStartMs?: number;
  /** Send an `auto_boundary` to the server. It answers with state, or an error. */
  ask: (req: { mode: TravelMode; center: LngLat; lines?: number[][][] }) => void;
  /** The socket's last error, so a refusal can stop the spinner and say why. */
  lastError?: { code: string; message: string; ts: number } | null;
  /** The boundary currently in state: when it changes, the build landed. */
  boundaryToken?: unknown;
}) {
  const { center, mode, origin, enabled, ask } = opts;
  const headStartMs = opts.headStartMs ?? DEFAULT_SETTINGS.headStartMs;
  const [building, setBuilding] = useState(false);
  const [failed, setFailed] = useState(false);
  const [shortCoverage, setShortCoverage] = useState(false);
  // v25 BOLT-144: WHY it failed, so the panel can offer the step that helps.
  const [reason, setReason] = useState<TurfFailure>('not-enclosed');
  // v25 BOLT-144: what the area we last built is made of, from ITS OWN ring.
  const [composition, setComposition] = useState<string[]>([]);

  // The callback is almost always an inline arrow at the call site, so it has a
  // NEW identity every render. Holding it in a ref keeps it out of the effect's
  // dependencies: without this the effect re-ran on every render, re-sent
  // `set_boundary` in a loop, and (because the server caches derived work per
  // BOUNDARY OBJECT) invalidated that cache on every frame. It quadrupled the
  // E2E suite's runtime once v24 started doing real work per boundary.
  const askRef = useRef(ask);
  askRef.current = ask;

  const build = useCallback(async () => {
    if (!center || !enabled) return;
    setBuilding(true);
    setFailed(false);
    setShortCoverage(false);

    // v26 BOLT-145: the CLIENT no longer decides. It asks, and the server
    // harvests its own barriers from pinned z14 tiles and answers.
    //
    // This is the whole fix for "web and iOS disagree at the same spot": both
    // clients used to scrape barriers out of the map they were RENDERING, so
    // the answer depended on camera, zoom and window size. There is one harvest
    // now and it belongs to the server.
    //
    // The ONE thing still sent is the keyless lattice. Without a MapTiler key
    // there is no `omt` source to harvest from on either side, and CI runs
    // keyless by design - so the synthetic grid keeps every flow exercisable.
    // With a key, `lines` is omitted entirely and the server ignores it anyway.
    const map = (window as unknown as { __boltMap?: MlMap }).__boltMap;
    const hasVectorSource = (() => {
      try {
        return !!map?.getSource?.('omt');
      } catch {
        return false;
      }
    })();
    const preset = barrierPresetFor(mode);
    const lines = hasVectorSource
      ? undefined
      : syntheticBarriers(center, preset.barriers, preset.harvestRadiusM).map(
          (l) => l.geometry.coordinates as number[][],
        );

    askRef.current({ mode, center, lines });
  }, [center, mode, enabled]);

  // v26 BOLT-145: the answer arrives over the socket now, so the spinner is
  // cleared by the RESULT rather than by the end of a local computation. A new
  // boundary means it landed; an auto_boundary error means it refused and the
  // reason travels so the panel can offer the step that helps.
  const boundaryToken = opts.boundaryToken;
  const seenBoundary = useRef(boundaryToken);
  useEffect(() => {
    if (boundaryToken === seenBoundary.current) return;
    seenBoundary.current = boundaryToken;
    setBuilding(false);
    setFailed(false);
    setShortCoverage(false);
  }, [boundaryToken]);

  const lastError = opts.lastError ?? null;
  const seenErrorTs = useRef(lastError?.ts ?? 0);
  useEffect(() => {
    if (!lastError || lastError.ts === seenErrorTs.current) return;
    seenErrorTs.current = lastError.ts;
    if (lastError.code === 'need_coverage') {
      setBuilding(false);
      setShortCoverage(true);
      setReason('need-coverage');
      setFailed(true);
      return;
    }
    if (lastError.code !== 'no_area') return;
    setBuilding(false);
    setShortCoverage(false);
    // The REASON travels in the message body (v25 BOLT-144), so the panel can
    // offer widen / coarser / draw rather than one dead-end sentence.
    const known: TurfFailure[] = ['not-enclosed', 'too-large', 'no-barriers', 'too-dense', 'degenerate', 'need-coverage'];
    const why = lastError.message as TurfFailure;
    setReason(known.includes(why) ? why : 'not-enclosed');
    setFailed(true);
  }, [lastError]);

  // Rebuild only when the INPUTS actually change. A moving GPS fix would
  // otherwise redraw the play area under the player's feet, so the centre is
  // rounded to ~10 m before it counts as "different".
  const centerKey = center ? `${center.lat.toFixed(4)},${center.lng.toFixed(4)}` : '';
  const inputs = `${origin}|${mode}|${centerKey}|${enabled}|${headStartMs}`;
  const lastInputs = useRef<string | null>(null);
  useEffect(() => {
    if (origin !== 'auto' || !enabled) return;
    if (lastInputs.current === inputs) return;
    lastInputs.current = inputs;
    build();
    // `build` is deliberately not a dependency: `inputs` is the real trigger,
    // and including it would reintroduce the render loop above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, origin, enabled]);

  return {
    building,
    failed,
    shortCoverage,
    reason,
    composition,
    rebuild: () => {
      lastInputs.current = null; // an explicit redraw always runs
      build();
    },
  };
}
